import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onAccordionClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onAccordionClick);
  }

  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  #onAccordionClick = (event) => {
    if (!this.#isMobile() || !(event.target instanceof Element)) return;
    const summary = event.target.closest('accordion-custom > details > summary');
    const details = summary?.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;

    requestAnimationFrame(() => {
      if (!details.open) return;
      const menu = details.closest('.menu-drawer__menu');
      menu?.querySelectorAll(':scope > li accordion-custom > details[open]').forEach((sibling) => {
        if (sibling !== details && sibling instanceof HTMLDetailsElement) {
          sibling.open = false;
          sibling.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      });
    });
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (this.#isMobile()) {
      // Root overflow locking breaks sticky positioning in mobile Safari.
      document.documentElement.removeAttribute('scroll-lock');
      const drawer = details.querySelector('[data-header-drawer]');
      const header = this.closest('#header-component');
      const headerRow = header?.querySelector('.header__row--top');
      if (drawer instanceof HTMLElement && headerRow instanceof HTMLElement) {
        const headerBounds = headerRow.getBoundingClientRect();
        const headerBottom = Math.max(0, Math.round(headerBounds.bottom));
        drawer.style.setProperty('--menu-drawer-mobile-top', `${headerBottom}px`);
      }
    }

    details.open = true;
    summary.setAttribute('aria-expanded', 'true');
    const drawer = details.querySelector('[data-header-drawer]');

    if (this.#isMobile() && drawer instanceof HTMLElement) {
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        this.closest('#header-component')?.setAttribute('data-mobile-menu-open', '');
      });
    } else {
      requestAnimationFrame(() => details.classList.add('menu-open'));
    }

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    const completeClose = () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    if (details === this.refs.details && this.#isMobile()) {
      const drawer = details.querySelector('[data-header-drawer]');
      this.closest('#header-component')?.removeAttribute('data-mobile-menu-open');

      if (drawer instanceof HTMLElement) {
        let fallbackTimer;
        const finishMobileClose = (event) => {
          if (event && event.target !== drawer) return;
          drawer.removeEventListener('animationend', finishMobileClose);
          if (fallbackTimer) window.clearTimeout(fallbackTimer);
          details.classList.remove('menu-open', 'menu-closing');
          completeClose();
        };

        drawer.addEventListener('animationend', finishMobileClose);
        details.classList.add('menu-closing');
        fallbackTimer = window.setTimeout(finishMobileClose, 450);
        return;
      }
    }

    details.classList.remove('menu-open');
    onAnimationEnd(details, completeClose);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
